import { ChangeEvent, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Select,
  Button,
  CardMedia,
  IconButton,
  Input,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  LocalizationProvider,
  DatePicker,
  DateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditText from "../../components/editText";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import {
  PROMOTION_REWARD_TYPE,
  REWARDSPROMOTIONS_TYPE,
} from "../../constants/enums";
import { UploadMedia } from "../../utils/uploadMedia";
import {
  useAddPromotionMutation,
  useGetPromotionByIdQuery,
  useUpdatePromotionMutation,
} from "../../services/promotions";
import usePermission from "../../hooks/userAccess";
import { showToast, showError } from "../../helpers";

type RewardsFormValues = {
  image: string;
  title: string;
  title_ar: string;
  previewText: string;
  url: string;
  shopOnline: boolean;
  discountValue: string | number;
  cashbackValue: string | number;
  startFrom: string | null;
  expireAt: string | null;
  description: string;
  description_ar: string;
  termsConditions: string;
  termsConditions_ar: string;
  rewardsPromotionsType: string | number;
};

const AddRewards = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { isAdd } = usePermission({
    label: "Rewards & Offers Management",
    page_permission: "isAdd",
  });

  const { data: promoRes, isLoading: isFetching } = useGetPromotionByIdQuery(
    id ? { id } : ({ id: "" } as any),
    { skip: !id }
  );

  const [addPromotion, { isLoading: isSaving }] = useAddPromotionMutation();
  const [updatePromotion, { isLoading: isUpdating }] =
    useUpdatePromotionMutation();

  // File name for uploaded image preview only
  const [fileName, setFileName] = useState<string>("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);

  // Initialize initialValues based on promoRes or defaults
  const initialValues: RewardsFormValues = {
    image: promoRes?.data?.image || "",
    title: promoRes?.data?.title || "",
    title_ar: promoRes?.data?.title_ar || "",
    previewText: promoRes?.data?.previewText || "",
    url: promoRes?.data?.url || "",
    shopOnline: Boolean(promoRes?.data?.shopOnline) || false,
    discountValue: promoRes?.data?.discountValue || "",
    cashbackValue: promoRes?.data?.cashbackValue || "",
    startFrom: promoRes?.data?.startFrom || null,
    expireAt: promoRes?.data?.expireAt || null,
    description: promoRes?.data?.description || "",
    description_ar: promoRes?.data?.description_ar || "",
    termsConditions: promoRes?.data?.termsConditions || "",
    termsConditions_ar: promoRes?.data?.termsConditions_ar || "",
    rewardsPromotionsType: promoRes?.data?.type || "default",
  };

  // Prefill fileName when promoRes is available
  useEffect(() => {
    if (promoRes) {
      setFileName(promoRes?.data?.image ? "Uploaded Image" : ""); // Set a default file name or extract from URL if needed
    }
  }, [promoRes]);

  const validationSchema = Yup.object({
    image: Yup.string().required("Image is required"),
    title: Yup.string().required("Reward Title is required"),
    title_ar: Yup.string().required("Arabic Reward Title is required"),
    previewText: Yup.string().required("Preview Text is required"),
    // url: Yup.string()
    //   .required("Image URL is required")
    //   .url("Please enter a valid URL"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters"),
    description_ar: Yup.string()
      .required("Arabic Description is required")
      .min(10, "Arabic Description must be at least 10 characters"),
    // termsConditions: Yup.string()
    //   .required("Terms & Conditions are required")
    //   .min(20, "Terms & Conditions must be at least 20 characters"),
    // termsConditions_ar: Yup.string()
    //   .required("Arabic Terms & Conditions are required")
    //   .min(20, "Arabic Terms & Conditions must be at least 20 characters"),
    startFrom: Yup.date().required("Valid start date is required"),
    expireAt: Yup.date().required("Valid end date is required"),
    rewardsPromotionsType: Yup.mixed().notOneOf(
      ["default"],
      "Please select a type"
    ),
  });

  const handleSubmit = async (
    values: RewardsFormValues,
    _helpers?: FormikHelpers<RewardsFormValues>
  ) => {
    const { rewardsPromotionsType, ...restValues } = values;
    const body = {
      ...restValues,
      previewText: values.previewText,
      description: values.description,
      description_ar: values.description_ar,
      termsConditions: values.termsConditions,
      termsConditions_ar: values.termsConditions_ar,
      cashbackValue: values.cashbackValue ? Number(values.cashbackValue) : 0,
      discountValue: values.discountValue ? Number(values.discountValue) : 0,
      url: values.url?.trim() || "",
      shopOnline: Boolean(values.shopOnline),
      type:
        values.rewardsPromotionsType !== "default"
          ? Number(values.rewardsPromotionsType)
          : PROMOTION_REWARD_TYPE.REWARDS,
    };
    try {
      if (
        !values.termsConditions?.length ||
        !values.description?.length ||
        !values.description_ar?.length ||
        !values.termsConditions_ar?.length
      ) {
        setAlertType(0);
        setShowAlert(true);
        setAlertMessage(
          "English & Arabic Description and Terms & Conditions are required."
        );
        return;
      }

      if (id) {
        await updatePromotion({ body: { ...body, id } as any }).unwrap();
        showToast("Rewards & Offers updated successfully");
      } else {
        await addPromotion(body as any).unwrap();
        showToast("Rewards & Offers added successfully");
      }

      navigate(-1);
    } catch (error: any) {
      setAlertType(0);
      setShowAlert(true);
      setAlertMessage("An error occurred while saving. Please try again.");
    }
  };

  const handleImageUpload = async (file: any, setFieldValue: any) => {
    const res = await UploadMedia(file);
    const imageUrl = res?.data?.images?.[0]?.url || res?.data?.image || "";
    if (imageUrl) {
      setFieldValue("image", imageUrl);
      setFileName(file.name); // Update fileName on new upload
    }
  };

  // Optional: Show a loading state while fetching promoRes
  if (isFetching && id) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="main_title">
        <h1>{id ? "Edit Reward" : "Add Rewards & Offers"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-rewards")}>
            Rewards & Offers Management
          </button>{" "}
          - {id ? "Edit Reward" : "Add Rewards & Offers"}
        </p>
      </div>

      <div className="cards">
        <Formik<RewardsFormValues>
          enableReinitialize // Required to update initialValues when promoRes changes
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form className="form">
              <div className="gap_p">
                {/* Banner Image */}
                <div className="control_group w_100">
                  <div className="gap_p">
                    <div className="control_group w_33">
                      <label htmlFor="icon-button-file">
                        Upload Image <span style={{ color: "red" }}>*</span>
                      </label>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "5px",
                          marginBottom: "8px",
                          fontWeight: "normal",
                        }}
                      >
                        For better visualization please add images in: 1080 ×
                        1080 px to 1920 × 1080 px
                      </p>
                      {values.image ? (
                        <div className="upload_image v2 no_radius">
                          <div className="upload_image_holder">
                            <figure>
                              <CardMedia
                                component="img"
                                image={values.image}
                                alt="Uploaded promotion image"
                              />
                            </figure>
                            <IconButton
                              aria-label="Remove uploaded image"
                              onClick={() => {
                                setFieldValue("image", "");
                                setFileName("");
                              }}
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        <label
                          className="upload_image v2 no_radius"
                          htmlFor="icon-button-file"
                        >
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{
                              accept: "image/png,image/jpeg,image/jpg",
                            }}
                            onChange={(
                              event: ChangeEvent<HTMLInputElement>
                            ) => {
                              const files = (event.target as HTMLInputElement)
                                .files;
                              if (files && files[0]) {
                                if (files[0].type.includes("image")) {
                                  if (files[0].size > 5 * 1024 * 1024) {
                                    setAlertType(0);
                                    setShowAlert(true);
                                    setAlertMessage(
                                      "Image size must be less than 5MB."
                                    );
                                    return;
                                  }
                                  setFileName(files[0].name);
                                  handleImageUpload(files[0], setFieldValue);
                                } else {
                                  setAlertType(0);
                                  setShowAlert(true);
                                  setAlertMessage(
                                    "This field only accepts images (PNG, JPEG, JPG)."
                                  );
                                }
                              }
                            }}
                          />
                          <span className="upload_image_holder">
                            <figure className="add_img">
                              <img
                                src="/static/images/placeholder.png"
                                alt="placeholder"
                              />
                              <span className="upload_img_icon">
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt="Upload"
                                />
                              </span>
                            </figure>
                          </span>
                        </label>
                      )}
                      {touched.image && errors.image && (
                        <FormHelperText error>
                          {String(errors.image)}
                        </FormHelperText>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="control_group w_50">
                  <label htmlFor="title">
                    Reward Title <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    id="title"
                    name="title"
                    placeholder="Enter Reward Title"
                    fullWidth
                    value={values.title}
                    onChange={handleChange}
                    error={touched.title && Boolean(errors.title)}
                    helperText={
                      touched.title && errors.title
                        ? String(errors.title)
                        : undefined
                    }
                  />
                </div>

                {/* Image URL */}
                <div className="control_group w_50">
                  <label htmlFor="url">Image URL</label>
                  <TextField
                    id="url"
                    name="url"
                    placeholder="https://example.com/image.jpg"
                    fullWidth
                    value={values.url}
                    onChange={handleChange}
                    error={touched.url && Boolean(errors.url)}
                    helperText={
                      touched.url && errors.url ? String(errors.url) : undefined
                    }
                  />
                </div>

                {/* Arabic Title */}
                <div className="control_group w_50">
                  <label htmlFor="title_ar">
                    Arabic Reward Title <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    id="title_ar"
                    name="title_ar"
                    placeholder="Enter Arabic Reward Title"
                    fullWidth
                    value={values.title_ar}
                    onChange={handleChange}
                    error={touched.title_ar && Boolean(errors.title_ar)}
                    helperText={
                      touched.title_ar && errors.title_ar
                        ? String(errors.title_ar)
                        : undefined
                    }
                  />
                </div>

                {/* Preview Text */}
                <div className="control_group w_50">
                  <label htmlFor="previewText">
                    Preview Text <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    id="previewText"
                    name="previewText"
                    placeholder="Enter Preview Text"
                    fullWidth
                    value={values.previewText}
                    onChange={handleChange}
                    error={touched.previewText && Boolean(errors.previewText)}
                    helperText={
                      touched.previewText && errors.previewText
                        ? String(errors.previewText)
                        : undefined
                    }
                  />
                </div>

                {/* Shop Online */}
                <div className="control_group w_50">
                  <label>Shop Online</label>
                  <FormGroup row sx={{ columnGap: "16px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(values.shopOnline)}
                          onChange={() => setFieldValue("shopOnline", true)}
                          name="shopOnlineYes"
                        />
                      }
                      label="Yes"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!Boolean(values.shopOnline)}
                          onChange={() => setFieldValue("shopOnline", false)}
                          name="shopOnlineNo"
                        />
                      }
                      label="No"
                    />
                  </FormGroup>
                </div>

                {/* Rewards Promotions Type */}
                <div className="control_group w_50">
                  <label htmlFor="rewardsPromotionsType">
                    Type <span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    id="rewardsPromotionsType"
                    name="rewardsPromotionsType"
                    fullWidth
                    value={values.rewardsPromotionsType}
                    onChange={handleChange}
                    error={
                      touched.rewardsPromotionsType &&
                      Boolean(errors.rewardsPromotionsType)
                    }
                  >
                    <MenuItem disabled value="default">
                      Select Type
                    </MenuItem>
                    <MenuItem value={REWARDSPROMOTIONS_TYPE.REWARDS}>
                      Offers & Rewards
                    </MenuItem>
                    <MenuItem value={REWARDSPROMOTIONS_TYPE.PROMOTIONS}>
                      Partner Engagement
                    </MenuItem>
                  </Select>
                  {touched.rewardsPromotionsType &&
                    errors.rewardsPromotionsType && (
                      <FormHelperText error>
                        {String(errors.rewardsPromotionsType)}
                      </FormHelperText>
                    )}
                </div>

                {/* Schedule Promotion */}
                <div className="control_group w_100">
                  <label htmlFor="schedule">Schedule Promotion</label>
                  <div className="gap_p">
                    <div className="control_group w_50">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          className="form_control"
                          slots={{
                            openPickerIcon: () => (
                              <img
                                src="/static/images/date_icon.svg"
                                alt="Pick date"
                              />
                            ),
                          }}
                          slotProps={{
                            textField: {
                              id: "startFrom",
                              placeholder: "Select Start Date & Time",
                              fullWidth: true,
                              hiddenLabel: true,
                              error:
                                touched.startFrom && Boolean(errors.startFrom),
                              helperText:
                                touched.startFrom && errors.startFrom
                                  ? String(errors.startFrom)
                                  : undefined,
                            },
                          }}
                          desktopModeMediaQuery="(min-width:0px)"
                          value={
                            values.startFrom ? dayjs(values.startFrom) : null
                          }
                          onChange={(v) =>
                            setFieldValue(
                              "startFrom",
                              v ? (v as Dayjs).toISOString() : ""
                            )
                          }
                          minDateTime={dayjs().startOf("day")}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="control_group w_50">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          className="form_control"
                          slots={{
                            openPickerIcon: () => (
                              <img
                                src="/static/images/date_icon.svg"
                                alt="Pick date"
                              />
                            ),
                          }}
                          slotProps={{
                            textField: {
                              id: "expireAt",
                              placeholder: "Select End Date & Time",
                              fullWidth: true,
                              hiddenLabel: true,
                              error:
                                touched.expireAt && Boolean(errors.expireAt),
                              helperText:
                                touched.expireAt && errors.expireAt
                                  ? String(errors.expireAt)
                                  : undefined,
                            },
                          }}
                          desktopModeMediaQuery="(min-width:0px)"
                          value={
                            values.expireAt ? dayjs(values.expireAt) : null
                          }
                          onChange={(v) =>
                            setFieldValue(
                              "expireAt",
                              v ? (v as Dayjs).toISOString() : ""
                            )
                          }
                          minDateTime={
                            values.startFrom
                              ? dayjs(values.startFrom).add(1, "hour")
                              : dayjs().startOf("day")
                          }
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="control_group w_100">
                  <label htmlFor="description">
                    Description <span style={{ color: "red" }}>*</span>
                  </label>
                  {isEdit && !values.description ? null : (
                    <EditText
                      content={values.description || " "}
                      setContent={(val: string) => {
                        setFieldValue("description", val);
                        setFieldTouched("description", true, true);
                      }}
                    />
                  )}
                  {touched.description && errors.description && (
                    <FormHelperText error>
                      {String(errors.description)}
                    </FormHelperText>
                  )}
                </div>

                {/* Arabic Description */}
                <div className="control_group w_100">
                  <label htmlFor="description_ar">
                    Arabic Description <span style={{ color: "red" }}>*</span>
                  </label>
                  {/* {isEdit && !values.description_ar ? null : ( */}
                  <EditText
                    content={values.description_ar}
                    setContent={(val: string) => {
                      setFieldValue("description_ar", val);
                      setFieldTouched("description_ar", true, true);
                    }}
                  />
                  {/* )} */}
                  {touched.description_ar && errors.description_ar && (
                    <FormHelperText error>
                      {String(errors.description_ar)}
                    </FormHelperText>
                  )}
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="control_group w_100" style={{ direction: "ltr" }}>
                <label htmlFor="termsConditions">Terms & Conditions</label>
                {/* {isEdit && !values.termsConditions ? null : ( */}
                <EditText
                  content={values.termsConditions || " "}
                  setContent={(val: string) => {
                    setFieldValue("termsConditions", val);
                    setFieldTouched("termsConditions", true, true);
                  }}
                />
                {/* )} */}
                {touched.termsConditions && errors.termsConditions && (
                  <FormHelperText error>
                    {String(errors.termsConditions)}
                  </FormHelperText>
                )}
              </div>

              {/* Arabic Terms & Conditions */}
              <div className="control_group w_100">
                <label htmlFor="reward_terms_ar">
                  Arabic Terms & Conditions
                </label>
                {/* {isEdit && !values.termsConditions_ar ? null : ( */}
                <EditText
                  content={values.termsConditions_ar}
                  setContent={(val: string) => {
                    setFieldValue("termsConditions_ar", val);
                    setFieldTouched("termsConditions_ar", true, true);
                  }}
                />
                {/* )} */}
                {touched.termsConditions_ar && errors.termsConditions_ar && (
                  <FormHelperText error>
                    {String(errors.termsConditions_ar)}
                  </FormHelperText>
                )}
              </div>

              {isAdd && (
                <div className="form_btn">
                  <Button
                    disabled={isSaving || isUpdating}
                    className="btn btn_primary"
                    type="submit"
                  >
                    {id ? "Update" : "Submit"}
                  </Button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default AddRewards;
